// Shared by calendar and team-dashboard time-zone selectors in both apps.
export function getTimeZoneOptions(selectedTimeZone = null, now = new Date()) {

  // "Most well-known" reps we prefer to display for each offset group
  // (ranked from most preferred -> least)
  const WELL_KNOWN_TZ = [
    "Pacific/Honolulu",
    "America/Anchorage",
    "America/Los_Angeles",
    "America/Denver",
    "America/Chicago",
    "America/New_York",
    "America/Toronto",
    "America/Halifax",
    "America/Sao_Paulo",
    "Atlantic/Azores",
    "Europe/London",
    "Europe/Paris",
    "Europe/Berlin",
    "Europe/Athens",
    "Africa/Cairo",
    "Europe/Moscow",
    "Asia/Dubai",
    "Asia/Karachi",
    "Asia/Kolkata",
    "Asia/Dhaka",
    "Asia/Bangkok",
    "Asia/Singapore",
    "Asia/Shanghai",
    "Asia/Tokyo",
    "Australia/Sydney",
    "Pacific/Auckland",
  ];

  const getOffsetMinutes = (date, timeZone) => {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

    const parts = dtf.formatToParts(date).reduce((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});

    const asUTC = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second)
    );

    return (asUTC - Math.floor(date.getTime() / 1000) * 1000) / 60000;
  };

  const pad2 = (n) => String(n).padStart(2, "0");

  const formatOffsetLabel = (offsetMin) => {
    const sign = offsetMin >= 0 ? "+" : "-";
    const abs = Math.abs(offsetMin);
    const hh = Math.floor(abs / 60);
    const mm = Math.floor(abs % 60);
    return `UTC${sign}${pad2(hh)}:${pad2(mm)}`;
  };

  const prettyCity = (tz) => {
    // Keep it simple + recognizable:
    // "America/New_York" -> "New York"
    // "Europe/London" -> "London"
    const parts = String(tz).split("/");
    const last = parts[parts.length - 1] || tz;
    return last.replace(/_/g, " ");
  };

  // Group zones by current offset
  const all = Intl.supportedValuesOf("timeZone");
  // Include valid device/URL zones and aliases even when they aren't listed by
  // supportedValuesOf (for example UTC). A select must contain its actual value.
  if (selectedTimeZone && !all.includes(selectedTimeZone)) {
    try {
      new Intl.DateTimeFormat("en-US", {timeZone: selectedTimeZone}).format(now);
      all.push(selectedTimeZone);
    } catch { /* Ignore invalid external values. */ }
  }
  const byOffset = new Map(); // offsetMin -> [tz, tz, ...]

  all.forEach((tz) => {
    const off = getOffsetMinutes(now, tz);
    if (!byOffset.has(off)) byOffset.set(off, []);
    byOffset.get(off).push(tz);
  });

  // Pick a representative tz for each offset group (prefer WELL_KNOWN_TZ)
  const pickRepresentative = (tzList) => {
    const set = new Set(tzList);
    if (selectedTimeZone && set.has(selectedTimeZone)) return selectedTimeZone;
    for (const candidate of WELL_KNOWN_TZ) {
      if (set.has(candidate)) return candidate;
    }
    // fallback: prefer "Region/City" style over weird ones if possible
    const normal = tzList.find((t) => String(t).includes("/") && !String(t).startsWith("Etc/"));
    return normal || tzList[0];
  };

  // Build compact options: one per offset, sorted by offset
  const offsetsSorted = Array.from(byOffset.keys()).sort((a, b) => a - b);

  return offsetsSorted.map((offsetMin) => {
    const tzList = byOffset.get(offsetMin) || [];
    const rep = pickRepresentative(tzList);

    // Add a couple helper cities from the group (excluding the representative)
    const helpers = tzList
      .filter((t) => t !== rep)
      .map(prettyCity);

    const uniqueHelpers = Array.from(new Set(helpers)).slice(0, 2);

    // Build slash-style label instead of "(e.g., ...)"
    const cityGroup = [prettyCity(rep), ...uniqueHelpers].join("/");

    return {
      value: rep,
      offsetMinutes: offsetMin,
      label: `${formatOffsetLabel(offsetMin)} — ${cityGroup}`,
    };
  });
}

// Retain the legacy export name, but preserve the actual zone instead of
// replacing it with a different city that happens to share its current offset.
export function mapTimeZoneToRepresentative(inputTz) {
  // Preserve IANA identity: equal offsets today need not share DST rules.
  for (const candidate of [inputTz, Intl.DateTimeFormat().resolvedOptions().timeZone, "UTC"]) {
    if (!candidate) continue;
    try {
      new Intl.DateTimeFormat("en-US", {timeZone: candidate}).format();
      return candidate;
    } catch { /* Try the device zone, then UTC. */ }
  }
  return "UTC";
}

